/**
 * Club chat.
 *
 * A floating button, visible only after sign-in. Own messages are dark and
 * right-aligned; committee messages carry a gold Admin badge. Typing `@`
 * opens a member dropdown — arrows navigate, Enter or Tab or click inserts,
 * and the resulting mention renders highlighted. SPEC.md §6.4.
 *
 * TEXT ONLY. Attaching an image was retired on 27 Aug 2026 (owner's
 * direction); messages already sent with one still render.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { bibNo, initials, timeAgo } from '../data';
import { useClubData } from '../context/ClubContext';
import { useUI } from '../context/UIContext';
import { Avatar } from './Avatar';
import { Empty } from './ui';
import { useBackToClose } from '../hooks/useBackToClose';

const MAX_MENTIONS = 6;
const MAX_INPUT_HEIGHT = 110;

/** Matches an `@` plus a partial name at the caret. */
const MENTION_AT_CARET = /@([A-Za-z ]{0,20})$/;

export function ChatFab({ onOpen }) {
  const { db, meId } = useClubData();
  const lastSeen = db.meta.chatSeen ?? 0;
  const unread = db.chat.filter((c) => c.ts > lastSeen && c.from !== meId).length;

  return (
    <button
      type="button"
      id="chatFab"
      /* Icon-only on a phone: as a labelled pill it sat on top of whatever
         full-width CTA was at the bottom of the page. */
      className="fixed bottom-5 right-4 z-[55] flex items-center gap-2 rounded-full bg-gold-500 p-3.5 font-market text-sm font-bold uppercase tracking-[0.05em] text-ink-800 transition-all duration-200 hover:brightness-105 sm:bottom-6 sm:right-6 sm:px-4 sm:py-3"
      onClick={onOpen}
    >
      <svg
        width="17"
        height="17"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9 9 0 0 1-3.3-.6L3 21l1.8-5a8.3 8.3 0 0 1-.8-3.6 8.4 8.4 0 0 1 8.5-8.4A8.4 8.4 0 0 1 21 11.5z" />
      </svg>
      <span className="max-sm:hidden">Club chat</span>
      {unread > 0 && (
        <span className="grid h-[18px] min-w-[18px] place-items-center rounded-full bg-flag-500 px-1 font-market text-[9.5px] font-semibold text-white max-sm:absolute max-sm:-right-0.5 max-sm:-top-0.5">
          {unread}
        </span>
      )}
    </button>
  );
}

export function ChatPanel({ onClose }) {
  useBackToClose(true, onClose);
  const { db, meId, role, sendChat, markChatSeen } = useClubData();
  const { toast } = useUI();

  const [text, setText] = useState('');
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionSel, setMentionSel] = useState(0);
  const [mentionList, setMentionList] = useState([]);

  const inputRef = useRef(null);
  const bodyRef = useRef(null);

  const messages = useMemo(() => [...db.chat].sort((a, b) => a.ts - b.ts), [db.chat]);

  // Opening the panel clears the unread badge.
  useEffect(() => {
    // Best-effort: a failed badge write is not worth interrupting them for.
    void markChatSeen().catch(() => {});
    const t = setTimeout(() => inputRef.current?.focus(), 150);
    return () => clearTimeout(t);
    // Only on mount — re-running would fight the badge on every new message.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pin to the newest message.
  useLayoutEffect(() => {
    const body = bodyRef.current;
    if (body) body.scrollTop = body.scrollHeight;
  }, [messages.length]);

  /** Recomputes the mention dropdown from whatever sits before the caret. */
  const refreshMentions = useCallback(
    (value, caret) => {
      const match = value.slice(0, caret).match(MENTION_AT_CARET);
      if (!match) {
        setMentionOpen(false);
        return;
      }
      const query = match[1].toLowerCase().trim();
      const matches = db.members
        .filter((m) => m.name.toLowerCase().includes(query))
        .slice(0, MAX_MENTIONS);

      setMentionList(matches);
      setMentionSel(0);
      setMentionOpen(matches.length > 0);
    },
    [db.members],
  );

  /** Replaces the partial `@name` at the caret with the full name. */
  function insertMention(member) {
    const input = inputRef.current;
    if (!input) return;

    const caret = input.selectionStart;
    const before = text.slice(0, caret).replace(MENTION_AT_CARET, `@${member.name} `);
    const next = before + text.slice(caret);

    setText(next);
    setMentionOpen(false);

    // Put the caret back after the inserted name, once React has painted.
    requestAnimationFrame(() => {
      input.focus();
      input.selectionStart = input.selectionEnd = before.length;
    });
  }

  async function send() {
    const body = text.trim();
    if (!body) return;

    // Clear the composer only once the message is actually saved. It used to
    // be wiped first, so a failed write lost what they had typed AND left the
    // message on screen as if it had sent.
    try {
      const mentioned = await sendChat(body);
      setText('');
      if (inputRef.current) inputRef.current.style.height = 'auto';
      if (mentioned > 0) toast(`Mentioned ${mentioned} member${mentioned > 1 ? 's' : ''}`);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not send that message');
    }
  }

  function onKeyDown(e) {
    if (mentionOpen && mentionList.length) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const step = e.key === 'ArrowDown' ? 1 : -1;
        setMentionSel((s) => (s + step + mentionList.length) % mentionList.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const picked = mentionList[mentionSel];
        if (picked) insertMention(picked);
        return;
      }
      if (e.key === 'Escape') {
        setMentionOpen(false);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  }

  return (
    <div
      id="chatPanel"
      /* Chat is a WIDGET, not a takeover: it sits in the bottom-right corner
         where the button was, and the page stays readable and clickable
         behind it. Only on a phone, where there is no room to sit beside
         anything, does it become a bottom sheet with a scrim. */
      className="fixed inset-0 z-[70] flex justify-end max-sm:bg-ink-900/60 max-sm:backdrop-blur-sm sm:inset-auto sm:bottom-6 sm:right-6"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div
        className="flex w-full flex-col overflow-hidden border-line bg-white motion-safe:animate-slidein max-sm:mt-16 sm:h-[min(600px,calc(100vh-6rem))] sm:w-[380px] sm:border sm:border-line sm:shadow-[0_30px_60px_-30px_rgba(11,15,24,.45)] sm:shadow-lift"
        role="dialog"
        aria-label="Club chat"
      >
        {/* The header is ink: it anchors the widget against the page behind it
            and gives the amber wordmark somewhere to sit. */}
        <div className="flex flex-none items-center justify-between bg-[linear-gradient(168deg,#161C2C_0%,#0D1220_52%,#0A0E19_100%)] px-4 py-3.5 text-white">
          <div>
            <div className="font-market text-[16px] font-extrabold tracking-[-0.02em]">
              Club <span className="text-gold-500">chat</span>
            </div>
            <div className="mt-0.5 font-market text-[9.5px] text-white/50">
              {db.members.length} members · type @ to mention
            </div>
          </div>
          <button
            type="button"
            className="border border-white/[.18] bg-white/[.05] px-2.5 py-1.5 font-market text-[10px] font-bold uppercase tracking-[0.14em] text-white/60 transition-colors duration-200 hover:border-gold-500/60 hover:text-gold-500"
            onClick={onClose}
          >
            Close
          </button>
        </div>
        <div className="h-1 flex-none bg-[linear-gradient(90deg,#F6C019,#7FD8C8)]" />

        <div className="flex flex-1 flex-col gap-3 overflow-y-auto bg-ground p-4" ref={bodyRef}>
          {messages.length === 0 ? (
            <Empty>No messages yet. Say hello.</Empty>
          ) : (
            messages.map((c) => {
              const isMine = c.from === meId && role === 'member';
              const isAdmin = c.admin || c.from === 'admin';
              const author = db.members.find((m) => m.id === c.from);

              return (
                <div
                  key={c.id}
                  className={`flex max-w-[88%] gap-2 ${isMine ? 'flex-row-reverse self-end' : ''}`}
                >
                  <div
                    className={`grid h-8 w-8 flex-none place-items-center overflow-hidden rounded-full font-market text-xs font-bold ${
                      isAdmin ? 'bg-ink-800 text-gold-500' : 'bg-line text-copy-muted'
                    }`}
                  >
                    {author?.photo ? (
                      <img
                        className="block h-full w-full rounded-full object-cover"
                        src={author.photo}
                        alt=""
                      />
                    ) : (
                      initials(c.name)
                    )}
                  </div>
                  <div
                    className={`min-w-0 px-3 py-2.5 ${
                      isMine ? 'bg-gold-500 text-ink-800' : 'border border-line bg-white'
                    }`}
                  >
                    <div
                      className={`mb-0.5 font-market text-[12.5px] font-bold uppercase tracking-[0.04em] ${
                        isMine ? 'text-right text-ink-800/70' : 'text-copy-muted'
                      }`}
                    >
                      {isMine ? 'You' : c.name}
                      {isAdmin && (
                        <span className="ml-1.5 rounded-full bg-gold-50 px-1.5 py-px align-[1px] font-market text-[7.5px] tracking-[0.08em] text-gold-800">
                          Admin
                        </span>
                      )}
                    </div>
                    {/* Only messages sent before 27 Aug 2026 carry one — the
                        attach button is gone, this renders the archive. */}
                    {c.image && (
                      <a
                        href={c.image}
                        target="_blank"
                        rel="noreferrer"
                        className="mb-1.5 block overflow-hidden"
                      >
                        <img
                          className="block max-h-[240px] w-full object-cover"
                          src={c.image}
                          alt="Shared in club chat"
                        />
                      </a>
                    )}
                    {c.text && (
                      <div className="break-words text-[13.5px] leading-relaxed">
                        <MentionText text={c.text} mine={isMine} />
                      </div>
                    )}
                    <div
                      className={`mt-1 font-market text-[9px] ${
                        isMine ? 'text-right text-ink-800/60' : 'text-copy-label'
                      }`}
                    >
                      {timeAgo(c.ts)}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="relative flex-none border-t border-line bg-white px-3 py-3">
          {mentionOpen && (
            <div className="absolute inset-x-3 bottom-full mb-2 max-h-[210px] overflow-y-auto border border-line bg-white">
              {mentionList.map((m, i) => (
                <div
                  key={m.id}
                  className={`flex cursor-pointer items-center gap-2.5 border-b border-line px-3 py-2.5 last:border-b-0 hover:bg-ground-warm ${
                    i === mentionSel ? 'bg-ground' : ''
                  }`}
                  onMouseDown={(e) => {
                    // mousedown, not click — the textarea must not blur first.
                    e.preventDefault();
                    insertMention(m);
                  }}
                  role="option"
                  aria-selected={i === mentionSel}
                >
                  <Avatar member={m} className="h-7 w-7 text-[11px]" />
                  <div className="text-[13.5px] font-medium">{m.name}</div>
                  <div className="ml-auto font-market text-[9.5px] text-copy-label">
                    bib {bibNo(m.bib)}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              className="max-h-[110px] min-h-[42px] flex-1 resize-none border border-line-strong px-3 py-2.5 font-sans text-sm leading-snug outline-none transition-all duration-200 focus:border-gold-500"
              rows={1}
              placeholder="Message the club…"
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = `${Math.min(MAX_INPUT_HEIGHT, e.target.scrollHeight)}px`;
                refreshMentions(e.target.value, e.target.selectionStart);
              }}
              onKeyDown={onKeyDown}
            />
            <button
              type="button"
              className="flex-none bg-gold-500 px-3.5 py-2.5 font-market text-sm font-bold uppercase tracking-[0.05em] text-ink-800 transition-all duration-200 hover:brightness-105"
              onClick={() => void send()}
            >
              Send
            </button>
          </div>
          <div className="mt-2 font-market text-[9.5px] text-copy-label">
            Type <b>@</b> to mention a member · Enter to send
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Highlights `@Name` runs inside a message.
 * Built from split parts rather than injected HTML — a member's message is
 * untrusted text and must never reach innerHTML.
 */
function MentionText({ text, mine }) {
  const parts = text.split(/(@[A-Za-z]+(?:\s[A-Za-z]+)?)/g);
  const chip = mine
    ? 'rounded bg-onAccent/15 px-1 font-semibold text-ink-800'
    : 'rounded bg-gold-50 px-1 font-semibold text-gold-800';
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith('@') ? (
          <span key={i} className={chip}>
            {part}
          </span>
        ) : (
          part
        ),
      )}
    </>
  );
}
